/**
 * PrivacyPage — public privacy policy.
 *
 * Required by the Google Play Store for any app that handles user data
 * (which we do — email, microphone audio, payment metadata). The Play
 * Console asks for a public URL; this page satisfies that requirement
 * when hosted at `${REACT_APP_BACKEND_URL}/privacy` on the production
 * web build.
 *
 * IMPORTANT — placeholder copy: replace the company name + contact email
 * with your real details before you publish to the store. The structure
 * + disclosures themselves match what Google Play and Apple App Review
 * actually look for (microphone use, audio uploads, third-party
 * processors, account data, retention).
 *
 * Hosting note: this page is publicly reachable WITHOUT auth — that's
 * intentional. Google's reviewer + your future users need to read it
 * before installing the app.
 */
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'April 29, 2026';
const COMPANY_NAME = 'Crowsearinv';                  // ⚠️ Replace with legal entity
const CONTACT_EMAIL = 'support@lingoflow.app';        // ⚠️ Replace with real address

export default function PrivacyPage() {
  return (
    <div className="lf-shell px-6 py-8 max-w-2xl mx-auto" data-testid="privacy-page">
      <Link
        to="/"
        className="text-xs text-[var(--lf-text-muted)] underline-offset-2 hover:underline"
        data-testid="privacy-home-link"
      >
        ← Back to LingoFlow
      </Link>
      <h1 className="mt-3 text-3xl font-bold">Privacy Policy</h1>
      <p className="mt-1 text-xs text-[var(--lf-text-muted)]">
        Last updated: {LAST_UPDATED}
      </p>

      <section className="mt-6 space-y-4 text-sm leading-relaxed">
        <p>
          This Privacy Policy describes how {COMPANY_NAME} ("we", "our", or
          "the Service") collects, uses, and protects your information when
          you use the LingoFlow app and website.
        </p>
      </section>

      <H2>Information we collect</H2>
      <Section>
        <P><b>Account data.</b> When you sign up, we store your email
        address, hashed password (using bcrypt — we never see your real
        password), display name, and optional profile picture. If you sign
        in via Google, we receive your name, email, and profile picture
        from Google.</P>
        <P><b>Learning data.</b> We store your CEFR level, lesson progress,
        vocabulary saves, conversation history with the AI tutor, and
        pronunciation scores so we can adapt the experience to you.</P>
        <P><b>Microphone audio.</b> When you tap the mic button, your spoken
        audio is captured by your device and sent to OpenAI's Whisper API
        for transcription. Audio is processed and discarded — neither we
        nor OpenAI retain it long-term. We store only the resulting text
        transcript on our servers.</P>
        <P><b>Usage data.</b> Anonymized session counts, button taps, and
        crash reports may be logged to help us improve the product.</P>
        <P><b>Payment data.</b> If you purchase a subscription via the
        web, payment is processed by PayPal. We never see your card number
        or PayPal account password — we only receive a transaction ID and
        amount.</P>
      </Section>

      <H2>How we use your information</H2>
      <Section>
        <P>To provide language-learning services (lesson generation,
        pronunciation feedback, progress tracking), to communicate with
        you about your account, to bill subscriptions, and to debug
        problems with the app.</P>
        <P>We do <b>NOT</b> sell your personal information. We do not
        share it with advertisers or data brokers.</P>
      </Section>

      <H2>Third-party processors</H2>
      <Section>
        <P>The following providers handle data on our behalf strictly to
        deliver the Service:</P>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>OpenAI</b> — speech transcription (Whisper), AI tutor
            replies (GPT-5.2), text-to-speech voice playback.</li>
          <li><b>Google</b> — optional sign-in via Emergent's managed
            Google Auth.</li>
          <li><b>PayPal</b> — payment processing for subscriptions.</li>
          <li><b>MongoDB Atlas</b> — encrypted database hosting.</li>
        </ul>
      </Section>

      <H2>Data retention &amp; deletion</H2>
      <Section>
        <P>You can delete your account and all associated data at any
        time from the Profile page or by emailing us at{' '}
        <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>
          {CONTACT_EMAIL}
        </a>. Account data is removed within 30 days of a deletion
        request. Anonymized usage statistics may be retained
        indefinitely.</P>
      </Section>

      <H2>Children</H2>
      <Section>
        <P>LingoFlow is rated for ages 13+. We do not knowingly collect
        personal data from children under 13. If you believe a child has
        provided data to us, please contact us so we can delete it.</P>
      </Section>

      <H2>Permissions on Android</H2>
      <Section>
        <P>The Android app requests these permissions:</P>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Microphone</b> — to capture your spoken practice
            answers.</li>
          <li><b>Internet</b> — to talk to our backend servers.</li>
          <li><b>Notifications</b> — for daily practice reminders (you can
            disable these in your device settings at any time).</li>
        </ul>
      </Section>

      <H2>Your rights</H2>
      <Section>
        <P>Depending on where you live, you may have the right to access,
        correct, export, or delete your personal data, and to opt out of
        certain processing. To exercise these rights, email{' '}
        <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>
          {CONTACT_EMAIL}
        </a>.</P>
      </Section>

      <H2>Changes to this policy</H2>
      <Section>
        <P>We may update this policy from time to time. The "Last
        updated" date at the top will reflect any change. Material
        changes will be communicated via email and an in-app notice.</P>
      </Section>

      <H2>Contact</H2>
      <Section>
        <P>Questions? Reach us at{' '}
          <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>.
        </P>
      </Section>

      <div className="mt-10 mb-8 text-xs text-[var(--lf-text-muted)] lf-bottom-safe">
        <p>
          See also:{' '}
          <Link to="/accessibility" className="underline">Accessibility Statement</Link>
        </p>
        <p className="mt-2">
          © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
        </p>
      </div>
    </div>
  );
}

function H2({ children }) {
  return (
    <h2 className="mt-7 text-lg font-bold border-b border-[var(--lf-border)] pb-1">
      {children}
    </h2>
  );
}

function Section({ children }) {
  return <div className="mt-3 space-y-2 text-sm leading-relaxed">{children}</div>;
}

function P({ children }) {
  return <p>{children}</p>;
}
